import { useMemo } from "react";
import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage.tsx";
import { models } from "types/api/viewModels.ts";
import { getAssignedToLocation } from "features/LeadGen/utils/leadAssignmentLocation.ts";
import { useAssignedToPersonNameLookup } from "./useAssignedToPersonNameLookup";
import { getAssignedToPersonName, getLeadFullName, getLeadStatusLabel } from "../utils";

export const useLeadFilters = (
	leads: models["LeadAssignmentViewModel"][],
	filterOptions: FilterOpts,
	searchValue: string
) => {
	const assignedToPersonNameLookup = useAssignedToPersonNameLookup();

	const filteredLeadsByFilters = useMemo(() => {
		return leads.filter((lead) => {
			const { assignedToLocation, assignedToPerson, brands, status } = filterOptions;

			const matchesAssignedToLocation =
				!assignedToLocation?.length || assignedToLocation.includes(getAssignedToLocation(lead) ?? "");
			const matchesAssignedToPerson =
				!assignedToPerson?.length ||
				assignedToPerson.includes(getAssignedToPersonName(lead, assignedToPersonNameLookup));
			const matchesBrand = !brands?.length || brands.includes(lead.lead?.brand ?? "");
			const matchesStatus = !status?.length || status.includes(getLeadStatusLabel(lead.status));

			return matchesAssignedToLocation && matchesAssignedToPerson && matchesBrand && matchesStatus;
		});
	}, [assignedToPersonNameLookup, leads, filterOptions]);

	const filteredLeads = useMemo(() => {
		if (!searchValue) return filteredLeadsByFilters;

		const searchLower = searchValue.toLowerCase();
		return filteredLeadsByFilters.filter((lead) => getLeadFullName(lead).toLowerCase().includes(searchLower));
	}, [filteredLeadsByFilters, searchValue]);

	const searchOptions = useMemo(
		() => filteredLeadsByFilters.map(getLeadFullName).filter(Boolean),
		[filteredLeadsByFilters]
	);

	return { filteredLeads, searchOptions };
};
